/**
 * Public-ish fill page for a form. Requires login + org membership.
 * Access controlled by form.access_user_ids / access_group_ids / access_role_ids
 * (admins always pass).
 */
import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { DynamicFormRenderer } from "@/components/app/DynamicFormRenderer";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, CheckCircle, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function FormFillPage() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [sections, setSections] = useState([]);
    const [fields, setFields] = useState([]);
    const [data, setData] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [accessDenied, setAccessDenied] = useState(false);
    const [groupDefaults, setGroupDefaults] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const check = await api.get(`/forms/${id}/access-check`);
                if (cancelled) return;
                if (!check.data.can_access) { setAccessDenied(true); setLoading(false); return; }
                const { data: meta } = await api.get(`/forms/${id}`);
                if (cancelled) return;
                setForm(meta.form); setSections(meta.sections); setFields(meta.fields);
                // Load per-form access defaults for the current user (from form_access_configs)
                try {
                    const { data: mine } = await api.get(`/forms/${id}/my-defaults`);
                    const merged = mine?.default_values || {};
                    setGroupDefaults(merged);
                    const seed = {};
                    meta.fields.forEach((f) => {
                        if (f.default_group_value_enabled && merged[f.field_name] !== undefined) {
                            seed[f.field_name] = merged[f.field_name];
                        } else if (f.default_value != null) {
                            seed[f.field_name] = f.default_value;
                        }
                    });
                    setData(seed);
                } catch (err) {
                    // No per-form access configs for this user — that's expected for admins; fall through to plain defaults.
                    if (err?.response?.status && err.response.status !== 403 && err.response.status !== 404) {
                        console.warn("[fill] /my-defaults failed:", err);
                    }
                }
            } catch (e) {
                toast.error(extractError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [id]);

    const lockedFields = useMemo(() => {
        const set = new Set();
        fields.forEach((f) => {
            if (f.default_group_value_enabled && groupDefaults[f.field_name] !== undefined) {
                set.add(f.field_name);
            }
        });
        return set;
    }, [fields, groupDefaults]);

    const submit = async (e) => {
        e?.preventDefault();
        setSubmitting(true);
        try {
            // visibility="everyone" tells backend to honor group's default_record_visibility if set
            await api.post(`/forms/${id}/records`, { data, visibility: "everyone" });
            setSubmitted(true);
            toast.success("Submitted");
        } catch (err) {
            toast.error(extractError(err));
        } finally { setSubmitting(false); }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center"><div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Loading form…</div></div>;
    }

    if (accessDenied) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 auth-grid-bg">
                <div className="brut-section p-12 max-w-md text-center" data-testid="access-denied">
                    <LockKey size={32} weight="duotone" className="mx-auto mb-4 text-destructive" />
                    <div className="label-overline mb-2">403</div>
                    <h1 className="text-2xl font-bold tracking-tight mb-2">Access denied</h1>
                    <p className="text-sm text-muted-foreground mb-6">You don't have access to this form. Ask the form owner to share it with you, your group, or your role.</p>
                    <Link to="/dashboard"><Button variant="outline" className="rounded-sm">Back to dashboard</Button></Link>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 auth-grid-bg">
                <div className="brut-section p-12 max-w-md text-center" data-testid="submitted-success">
                    <CheckCircle size={32} weight="duotone" className="mx-auto mb-4 text-primary" />
                    <div className="label-overline mb-2">Received</div>
                    <h1 className="text-2xl font-bold tracking-tight mb-2">Submission recorded</h1>
                    <p className="text-sm text-muted-foreground mb-6">Your response has been saved to {form?.form_name}.</p>
                    <div className="flex gap-2 justify-center">
                        <Button onClick={() => { setSubmitted(false); setData({}); }} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="submit-another-btn">Submit another</Button>
                        <Button variant="outline" onClick={() => navigate("/dashboard")} className="rounded-sm">Done</Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-secondary/30">
            <div className="bg-card border-b border-border">
                <div className="max-w-3xl mx-auto px-6 py-6">
                    <button onClick={() => navigate(-1)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3" data-testid="fill-back">
                        <ArrowLeft size={12} /> Back
                    </button>
                    <div className="flex items-center gap-2 mb-2">
                        <div className="label-overline">{form?.form_code}</div>
                        <Badge variant="outline" className="rounded-sm font-mono text-[10px]">{form?.status}</Badge>
                    </div>
                    <h1 className="text-3xl font-bold tracking-tight" data-testid="fill-title">{form?.form_title || form?.form_name}</h1>
                    {form?.description && <p className="text-sm text-muted-foreground mt-2">{form.description}</p>}
                </div>
            </div>
            <div className="max-w-3xl mx-auto p-6">
                <form onSubmit={submit} className="brut-section p-8 bg-card" data-testid="fill-form">
                    <DynamicFormRenderer
                        sections={sections} fields={fields}
                        data={data} onChange={setData}
                        lockedFields={lockedFields}
                        layoutMode={form?.layout_type || form?.layout_mode || "single"}
                    />
                    <div className="border-t border-border pt-6 mt-8 flex items-center justify-end">
                        <Button type="submit" disabled={submitting} className="rounded-sm h-11 px-8 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="fill-submit-btn">
                            {submitting ? "Submitting…" : "Submit"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
